import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  AlertTitle,
} from "@mui/material";
import { Upload as UploadIcon } from "@mui/icons-material";

const ImportTimestampsDialog = ({ open, onClose, onImport, tappingPoints, maxDurationSeconds = null }) => {
  const [pastedData, setPastedData] = useState("");
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);

  const handleClose = () => {
    setPastedData("");
    setErrors([]);
    setWarnings([]);
    onClose();
  };

  const parseTimestamp = (minutesStr, secondsStr) => {
    const minutes = parseFloat(minutesStr?.trim() || "0");
    const seconds = parseFloat(secondsStr?.trim() || "0");

    // Validation
    if (isNaN(minutes) || isNaN(seconds)) {
      return { error: "Invalid number format" };
    }

    if (minutes < 0 || seconds < 0) {
      return { error: "Minutes and seconds cannot be negative" };
    }

    if (seconds >= 60) {
      return { error: "Seconds must be less than 60" };
    }

    if (minutes > 60) {
      return { error: "Minutes cannot be greater than 60" };
    }

    const totalSeconds = Math.floor(minutes * 60 + seconds);

    // Check against max duration if provided
    if (maxDurationSeconds && totalSeconds > maxDurationSeconds) {
      return {
        error: `Timestamp ${minutes}:${seconds.toString().padStart(2, "0")} exceeds audio/video length`,
      };
    }

    return { totalSeconds };
  };

  const findTappingPoint = pointName => {
    if (!pointName?.trim()) {
      return { error: "Tapping point name is required" };
    }

    const cleanPointName = pointName.trim().toLowerCase();
    const tappingPoint = tappingPoints.find(tp => tp.tapping_point_name?.toLowerCase() === cleanPointName);

    if (!tappingPoint) {
      return { error: `Tapping point "${pointName}" not found in allowed list` };
    }

    return { tappingPoint };
  };

  const parseSpreadsheetData = data => {
    const lines = data.split("\n").filter(line => line.trim());
    const results = [];
    const parseErrors = [];
    const parseWarnings = [];

    lines.forEach((line, index) => {
      const lineNumber = index + 1;

      // Split by tab or comma (common spreadsheet separators)
      const columns = line.split(/\t|,/).map(col => col.trim());

      if (columns.length < 3) {
        parseErrors.push(`Line ${lineNumber}: Expected at least 3 columns (minutes, seconds, tapping point)`);
        return;
      }

      const [minutesStr, secondsStr, tappingPointName, ...extraColumns] = columns;

      // Parse timestamp
      const timestampResult = parseTimestamp(minutesStr, secondsStr);
      if (timestampResult.error) {
        parseErrors.push(`Line ${lineNumber}: ${timestampResult.error}`);
        return;
      }

      // Find tapping point
      const tappingPointResult = findTappingPoint(tappingPointName);
      if (tappingPointResult.error) {
        parseErrors.push(`Line ${lineNumber}: ${tappingPointResult.error}`);
        return;
      }

      // Warning for extra columns
      if (extraColumns.length > 0 && extraColumns.some(col => col.trim())) {
        parseWarnings.push(`Line ${lineNumber}: Extra data ignored: ${extraColumns.join(", ")}`);
      }

      results.push({
        timestamp: timestampResult.totalSeconds,
        tapping_point_id: tappingPointResult.tappingPoint.tapping_point_id,
        image_url: tappingPointResult.tappingPoint.tapping_point_image_url,
        originalLine: lineNumber,
        displayTime: `${Math.floor(timestampResult.totalSeconds / 60)}:${String(timestampResult.totalSeconds % 60).padStart(2, "0")}`,
        pointName: tappingPointResult.tappingPoint.tapping_point_name,
      });
    });

    return { results, errors: parseErrors, warnings: parseWarnings };
  };

  const handleImport = () => {
    if (!pastedData.trim()) {
      setErrors(["Please paste spreadsheet data before importing"]);
      return;
    }

    const { results, errors: parseErrors, warnings: parseWarnings } = parseSpreadsheetData(pastedData);

    setErrors(parseErrors);
    setWarnings(parseWarnings);

    if (parseErrors.length === 0 && results.length > 0) {
      onImport(results);
      handleClose();
    }
  };

  const availablePoints = tappingPoints
    ?.filter(tp => tp.tapping_point_name && tp.tapping_point_name !== "Default Tapping Point")
    ?.map(tp => tp.tapping_point_name)
    ?.join(", ");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <UploadIcon />
          Import Timestamps from Spreadsheet
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Paste your spreadsheet data here. Expected format: one row per timestamp with 3 columns:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", bgcolor: "grey.100", p: 1, borderRadius: 1 }}>
            Minutes&nbsp;&nbsp;&nbsp;&nbsp;Seconds&nbsp;&nbsp;&nbsp;&nbsp;Tapping Point Name
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can copy and paste directly from Excel, Google Sheets, or any spreadsheet application.
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={8}
          value={pastedData}
          onChange={e => setPastedData(e.target.value)}
          placeholder="Paste your spreadsheet data here...&#10;Example:&#10;1    30    Karate Chop&#10;2    15    Eyebrow&#10;3    45    Side of Eye"
          variant="outlined"
          sx={{ mb: 2 }}
        />

        {/* Available Tapping Points */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Available Tapping Points:
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxHeight: 100,
              overflow: "auto",
              bgcolor: "grey.50",
              p: 1,
              borderRadius: 1,
              fontSize: "0.875rem",
            }}
          >
            {availablePoints || "No tapping points available"}
          </Typography>
        </Box>

        {/* Warnings */}
        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Warnings</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Import Errors</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleImport} disabled={!pastedData.trim()}>
          Import Timestamps
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportTimestampsDialog;
